
import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Minus, Plus } from 'lucide-react';

interface QuantityControlProps {
  quantity: number;
  maxQuantity: number;
  onUpdate: (newQuantity: number) => Promise<void>;
  size?: 'sm' | 'default';
}

const QuantityControl = ({ quantity, maxQuantity, onUpdate, size = 'default' }: QuantityControlProps) => {
  const [displayQuantity, setDisplayQuantity] = useState(quantity);
  const [isUpdating, setIsUpdating] = useState(false);
  const pendingUpdateRef = useRef<number | null>(null);
  const lastConfirmedQuantityRef = useRef(quantity);

  // Synchroniser uniquement quand la prop change de l'extérieur (pas de notre propre mise à jour)
  useEffect(() => {
    // Si la quantité de la prop correspond à notre dernière mise à jour confirmée ou en attente, ignorer
    if (quantity === lastConfirmedQuantityRef.current || quantity === pendingUpdateRef.current) {
      return;
    }
    // Sinon, c'est un changement externe, on synchronise
    setDisplayQuantity(quantity);
    lastConfirmedQuantityRef.current = quantity;
  }, [quantity]);

  const handleUpdateQuantity = useCallback(async (newQuantity: number) => {
    if (newQuantity < 0 || newQuantity > maxQuantity || isUpdating) return;
    
    // Mettre à jour l'affichage immédiatement
    setDisplayQuantity(newQuantity);
    pendingUpdateRef.current = newQuantity;
    setIsUpdating(true);
    
    try {
      await onUpdate(newQuantity);
      lastConfirmedQuantityRef.current = newQuantity;
      console.log('Quantity updated successfully to:', newQuantity);
    } catch (error) {
      console.error('Failed to update quantity:', error);
      // Restaurer la dernière valeur confirmée en cas d'erreur
      setDisplayQuantity(lastConfirmedQuantityRef.current);
    } finally {
      pendingUpdateRef.current = null;
      setIsUpdating(false);
    }
  }, [maxQuantity, onUpdate, isUpdating]);

  const handleIncrement = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const newQuantity = Math.min(displayQuantity + 1, maxQuantity);
    if (newQuantity !== displayQuantity) {
      handleUpdateQuantity(newQuantity);
    }
  }, [displayQuantity, maxQuantity, handleUpdateQuantity]);

  const handleDecrement = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const newQuantity = Math.max(displayQuantity - 1, 0);
    if (newQuantity !== displayQuantity) {
      handleUpdateQuantity(newQuantity);
    }
  }, [displayQuantity, handleUpdateQuantity]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    const value = parseInt(e.target.value) || 0;
    const clampedValue = Math.max(0, Math.min(value, maxQuantity));
    if (clampedValue !== displayQuantity) {
      handleUpdateQuantity(clampedValue);
    }
  }, [maxQuantity, displayQuantity, handleUpdateQuantity]);

  const buttonSize = size === 'sm' ? 'h-6 w-6' : 'h-8 w-8';
  const inputSize = size === 'sm' ? 'h-6 w-16 text-xs' : 'h-8 w-20 text-sm';

  return (
    <div className="flex items-center gap-1">
      <Button
        variant="outline"
        size="sm"
        onClick={handleDecrement}
        disabled={displayQuantity <= 0 || isUpdating}
        className={`${buttonSize} p-0 flex-shrink-0`}
        type="button"
      >
        <Minus className="h-2 w-2" />
      </Button>
      
      <Input
        type="number"
        min="0"
        max={maxQuantity}
        value={displayQuantity}
        onChange={handleInputChange}
        disabled={isUpdating}
        className={`${inputSize} text-center border-1 bg-white font-medium flex-shrink-0 px-1`}
        onClick={(e) => e.stopPropagation()}
      />
      
      <Button
        variant="outline"
        size="sm"
        onClick={handleIncrement}
        disabled={displayQuantity >= maxQuantity || isUpdating}
        className={`${buttonSize} p-0 flex-shrink-0`}
        type="button"
      >
        <Plus className="h-2 w-2" />
      </Button>
    </div>
  );
};

export default QuantityControl;
